import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {Notification} from './notifications.component';

@Component({
    selector: 'vex-notification-detail-dialog',
    template: `
        <div class="p-6">
            <div class="flex items-center justify-between mb-4">
                <h2 class="text-xl font-semibold">{{ "NOTIFICATIONS.TITULO" | translate }}</h2>
                <button mat-icon-button (click)="close()" type="button">
                    <mat-icon svgIcon="mat:close"></mat-icon>
                </button>
            </div>

            <div class="space-y-4">
                <!-- Nombre del Usuario -->
                <div>
                    <label class="text-sm font-medium text-secondary block mb-1">Nombre</label>
                    <p class="text-base">{{ notification.userName || 'N/A' }}</p>
                </div>

                <!-- Mensaje -->
                <div>
                    <label class="text-sm font-medium text-secondary block mb-1">{{ "NOTIFICATIONS.MENSAJE" | translate }}</label>
                    <p class="text-base">{{ notification.message || '-' }}</p>
                </div>

                <!-- Estado de Lectura -->
                <div>
                    <label class="text-sm font-medium text-secondary block mb-1">{{ "NOTIFICATIONS.LEIDA" | translate }}</label>
                    <span *ngIf="notification.read_it" class="px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                        {{ "NOTIFICATIONS.LEIDAS" | translate }}
                    </span>
                    <span *ngIf="!notification.read_it" class="px-2 py-1 rounded text-xs bg-red-100 text-red-800">
                        {{ "NOTIFICATIONS.NO_LEIDAS" | translate }}
                    </span>
                </div>

                <!-- Tipo (si existe) -->
                <div *ngIf="notification.type">
                    <label class="text-sm font-medium text-secondary block mb-1">{{ "NOTIFICATIONS.TIPO" | translate }}</label>
                    <p class="text-base">{{ notification.type }}</p>
                </div>

                <!-- Prioridad (si existe) -->
                <div *ngIf="notification.priority">
                    <label class="text-sm font-medium text-secondary block mb-1">{{ "NOTIFICATIONS.PRIORIDAD" | translate }}</label>
                    <span class="px-2 py-1 rounded text-xs"
                          [ngClass]="{
                              'bg-gray-100 text-gray-800': !notification.priority || notification.priority === 'normal',
                              'bg-blue-100 text-blue-800': notification.priority === 'low',
                              'bg-yellow-100 text-yellow-800': notification.priority === 'high',
                              'bg-red-100 text-red-800': notification.priority === 'urgent'
                          }">
                        {{ getPriorityLabel(notification.priority) }}
                    </span>
                </div>

                <!-- Categoría (si existe) -->
                <div *ngIf="notification.category">
                    <label class="text-sm font-medium text-secondary block mb-1">{{ "NOTIFICATIONS.CATEGORIA" | translate }}</label>
                    <p class="text-base">{{ notification.category }}</p>
                </div>

                <!-- Fecha de Creación -->
                <div>
                    <label class="text-sm font-medium text-secondary block mb-1">{{ "NOTIFICATIONS.FECHA" | translate }}</label>
                    <p class="text-base">{{ formatDate(notification.created_at) }}</p>
                </div>

                <!-- Fecha de Actualización (si existe) -->
                <div *ngIf="notification.updated_at">
                    <label class="text-sm font-medium text-secondary block mb-1">Fecha de Actualización</label>
                    <p class="text-base">{{ formatDate(notification.updated_at) }}</p>
                </div>

                <!-- URL de Acción (si existe) -->
                <div *ngIf="notification.action_url || notification.actionUrl || notification.ActionURL">
                    <label class="text-sm font-medium text-secondary block mb-1">URL de Acción</label>
                    <a [href]="notification.action_url || notification.actionUrl || notification.ActionURL" 
                       target="_blank" 
                       class="text-blue-600 hover:underline">
                        {{ notification.action_url || notification.actionUrl || notification.ActionURL }}
                    </a>
                </div>
            </div>

            <!-- Acciones -->
            <div class="flex justify-end gap-2 mt-6 pt-4 border-t">
                <button *ngIf="!notification.read_it" 
                        mat-raised-button 
                        color="primary"
                        (click)="markAsRead()">
                    <mat-icon svgIcon="mat:mark_email_read"></mat-icon>
                    <span class="ml-2">{{ "NOTIFICATIONS.MARCAR_LEIDA" | translate }}</span>
                </button>
                <button *ngIf="notification.read_it" 
                        mat-raised-button 
                        color="accent"
                        (click)="markAsUnread()">
                    <mat-icon svgIcon="mat:mark_email_unread"></mat-icon>
                    <span class="ml-2">{{ "NOTIFICATIONS.MARCAR_NO_LEIDA" | translate }}</span>
                </button>
                <button mat-raised-button 
                        color="warn"
                        (click)="delete()">
                    <mat-icon svgIcon="mat:delete"></mat-icon>
                    <span class="ml-2">{{ "ACTIONS.ELIMINAR" | translate }}</span>
                </button>
                <button mat-button (click)="close()">
                    {{ "ACTIONS.CANCELAR" | translate }}
                </button>
            </div>
        </div>
    `
})
export class NotificationDetailDialogComponent implements OnInit {
    notification: Notification;

    constructor(
        public dialogRef: MatDialogRef<NotificationDetailDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: Notification,
        private translateService: TranslateService
    ) {
        this.notification = data;
    }

    ngOnInit(): void {
    }

    formatDate(dateString: string | undefined): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-CL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    getPriorityLabel(priority: string): string {
        const labels: { [key: string]: string } = {
            'low': 'BAJA',
            'normal': 'NORMAL',
            'high': 'ALTA',
            'urgent': 'URGENTE'
        };
        return labels[priority] || priority;
    }

    markAsRead() {
        this.dialogRef.close('markAsRead');
    }

    markAsUnread() {
        this.dialogRef.close('markAsUnread');
    }

    delete() {
        if (confirm('¿Está seguro de que desea eliminar esta notificación?')) {
            this.dialogRef.close('delete');
        }
    }

    close() {
        this.dialogRef.close();
    }
}

